import React, { memo } from 'react';
import intl from 'react-intl-universal';
import { connect } from 'react-redux';
import { Table, InputNumber, message } from 'antd';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import { ReducerState, storeName } from '@/redux/reducers/reservation/checkOut';
import { handlePeripheralReturnItemChange } from '@/redux/actions/reservation/checkOut';
import ContentBlock from '@/components/Reservation/ContentBlock';
import styles from './index.module.scss';

interface IChangeItem {
  type: 'add' | 'reduce' | 'change';
  skuCode: string;
  returnQty?: number;
}

const stateSelector = (state: ReducerState) => {
  const { peripheralRoomInventoryList } = state;
  return { peripheralRoomInventoryList };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callItemChange: handlePeripheralReturnItemChange,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IProps {
  refreshRoomCardInfo: () => void;
  refreshOrderDetail: () => void;
}

const Index: React.FC<IProps & MapStateProps & MapDispatchProps> = ($$) => {
  // 外设归还
  const changePeripheralReturnQty = ({ type, skuCode, returnQty }: IChangeItem) => {
    const peripheralList = JSON.parse(JSON.stringify($$.peripheralRoomInventoryList)) || [];
    switch (type) {
      case 'add':
        const newList1 = peripheralList.map((item: any) => {
          if (item.skuCode === skuCode) {
            // 达到最大值 归还大于等于上次剩余
            if (item.returnQty >= item.qty) {
              message.warning(intl.get('cmp_inventory_error_value_max'));
              return item;
            }
            return { ...item, returnQty: item.returnQty + 1 };
          }
          return item;
        });
        $$.callItemChange(newList1);
        break;
      case 'reduce':
        const newList2 = peripheralList.map((item) => {
          if (item.skuCode === skuCode) {
            // 达到最小值 剩余数量为0
            if (item.returnQty === 0) {
              message.warning(intl.get('cmp_inventory_error_value_min'));
              return item;
            }
            return { ...item, returnQty: item.returnQty - 1 };
          }
          return item;
        });
        $$.callItemChange(newList2);
        break;
      case 'change':
        const newList3 = peripheralList.map((item) => {
          if (item.skuCode === skuCode) {
            // 达到最大值 上次剩余不为空 归还数量大于上次剩余
            if (returnQty > item.returnQty) {
              message.warning(intl.get('cmp_inventory_error_value_invalid'));
              return item;
            }
            return { ...item, returnQty };
          }
          return item;
        });
        $$.callItemChange(newList3);
        break;
      default:
        break;
    }
  };

  const inputNumberConfirm = (e: any, record: any) => {
    const val = e.target.value;
    // 正整数校验
    if (!/(^[1-9]\d*$)/.test(val)) {
      message.warn(intl.get('goods_input_error_and_retry'));
    } else {
      changePeripheralReturnQty({
        type: 'change',
        skuCode: record.skuCode,
        returnQty: Number(val),
      });
    }
  };

  const tableColumns: any = [
    { title: '房号', dataIndex: 'roomNo', key: 'roomNo' },
    { title: '外设名称', dataIndex: 'spuName', key: 'spuName' },
    { title: '总租借次数', dataIndex: 'totalTimes', key: 'totalTimes', align: 'center' },
    { title: '房间库存', dataIndex: 'qty', key: 'qty', align: 'center' },
    {
      title: '归还数量',
      dataIndex: 'returnQty',
      key: 'returnQty',
      align: 'center',
      render: (value, record) => {
        return (
          <InputNumber
            value={value ?? 0}
            className={styles.inputNumber}
            min={0}
            // max={record?.qty || 0}
            onStep={(val, info) => {
              if (info.type === 'down') {
                changePeripheralReturnQty({ type: 'reduce', skuCode: record.skuCode });
                return;
              }
              if (info.type === 'up') {
                changePeripheralReturnQty({ type: 'add', skuCode: record.skuCode });
                return;
              }
              changePeripheralReturnQty({
                type: 'change',
                skuCode: record.skuCode,
                returnQty: val,
              });
            }}
            onPressEnter={(e) => inputNumberConfirm(e, record)}
            onBlur={(e) => inputNumberConfirm(e, record)}
          />
        );
      },
    },
  ];

  // 出现场景：换房的原房间有未归还的外设
  if (!$$.peripheralRoomInventoryList || $$.peripheralRoomInventoryList.length === 0) return null;

  return (
    <ContentBlock title="外设归还" className={styles.listContainer}>
      <Table
        pagination={false}
        className={styles.rentRoomContainer}
        rowKey="skuCode"
        columns={tableColumns}
        dataSource={$$.peripheralRoomInventoryList || []}
      />
    </ContentBlock>
  );
};

export default connect<MapStateProps, MapDispatchProps, IProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
